import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAppParams, useAppSelector, useAppDispatch } from '../hooks';
import { selectCurrentUser } from '../features/auth/authSlice';
import type { CachedLockInfo } from '../features/project/projectTypes';
import { useLazyGetProjectLockInfoQuery, useToggleProjectLockMutation } from '@app/features/project/projectApiSlice';
import { setLockInfo, clearLockInfo, selectLockInfo } from '../features/project/projectSlice';

interface HybridLockConfig {
  // Smart polling intervals
  lockedInterval: number;
  unlockedInterval: number;
  idleInterval: number;
  
  // Visibility and activity settings
  activityTimeout: number;
  pausedInterval: number;
  
  // Cache settings
  staleTime: number;
  cacheTime: number;
  
  // Limits
  maxInterval: number;
}

const DEFAULT_HYBRID_CONFIG: HybridLockConfig = {
  lockedInterval: 10000,       // 10s when locked
  unlockedInterval: 60000,   // 60s when unlocked or locked by current user
  idleInterval: 240000,      // 4min when idle
  activityTimeout: 120000,    // 2min to consider idle
  pausedInterval: 600000,    // 10min when tab hidden
  staleTime: 60000,          // 60s cache freshness
  cacheTime: 300000,         // 5min cache retention
  maxInterval: 600000,       // 10min max interval
};

// Module-level state for single-instance polling
let globalPollingTimeout: NodeJS.Timeout | null = null;
let hookInstanceCount = 0;
let currentProjectId: string | null = null;

export const useHybridLockStatus = (config: Partial<HybridLockConfig> = {}) => {
  const [toggleProjectLock] = useToggleProjectLockMutation();
  const dispatch = useAppDispatch();

  const { projectId } = useAppParams();
  const isProjectIdNumerical = !isNaN(Number(projectId));
  const currentUser = useAppSelector(selectCurrentUser);
  const cachedData = useAppSelector(selectLockInfo);
  
  const finalConfig = useMemo(() => ({ ...DEFAULT_HYBRID_CONFIG, ...config }), [config]);
  
  // State management
  const [isUserActive, setIsUserActive] = useState(true);
  const [isVisible, setIsVisible] = useState(!document.hidden);
  const [isLoading, setIsLoading] = useState(false);
  
  // Refs for cleanup
  const activityTimeoutRef = useRef<NodeJS.Timeout>();
  const lastActivityRef = useRef<number>(Date.now());
  const previousProjectIdRef = useRef<string | undefined>(undefined);
  
  // Activity tracking
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIsUserActive(true);
    
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    
    activityTimeoutRef.current = setTimeout(() => {
      setIsUserActive(false);
    }, finalConfig.activityTimeout);
  }, [finalConfig.activityTimeout]);
  
  // Visibility tracking
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  
  // Activity listeners
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, updateActivity, true);
    });
    
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity, true);
      });
      
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
    };
  }, [updateActivity]);
  
  // Calculate optimal polling interval
  const getOptimalInterval = useCallback((lockInfo: CachedLockInfo | null, currentUserEmail?: string) => {
    // If tab is not visible, use paused interval
    if (!isVisible) {
      return finalConfig.pausedInterval;
    }
    
    // If user is idle, use idle interval
    if (!isUserActive) {
      return finalConfig.idleInterval;
    }
    
    // If project is locked by someone else, poll more frequently
    if (lockInfo?.is_locked && lockInfo?.locked_by !== currentUserEmail) {
      return finalConfig.lockedInterval;
    }
    
    // If project is unlocked OR locked by current user, poll less frequently
    return finalConfig.unlockedInterval;
  }, [isVisible, isUserActive, finalConfig]);
  
  // Check if data is stale
  const isDataStale = useCallback((cachedAt: number) => {
    return Date.now() - cachedAt > finalConfig.staleTime;
  }, [finalConfig.staleTime]);

  const [callFetchLockInfo] = useLazyGetProjectLockInfoQuery();
  
  // Fetch function
  const fetchLockInfo = useCallback(async () => {
    if (!projectId || !isProjectIdNumerical) return;
    
    try {
      const result = await callFetchLockInfo(projectId);
      
      if (result.data) {
        dispatch(setLockInfo({
          projectId: Number(projectId),
          ...result.data,
          cachedAt: Date.now(),
          isStale: false,
        }));
      }
    } catch (error) {
      console.warn('Lock status fetch failed:', error);
    }
  }, [projectId, isProjectIdNumerical, callFetchLockInfo, dispatch]);
  
  // Release lock and clear cache when switching projects
  useEffect(() => {
    if (
      previousProjectIdRef.current &&
      previousProjectIdRef.current !== projectId &&
      cachedData?.is_locked &&
      cachedData?.locked_by === currentUser?.email
    ) {
      toggleProjectLock({
        projectId: Number(previousProjectIdRef.current),
        isLocked: false,
      }).unwrap().catch((err) => console.warn('Failed to release lock on project switch:', err));
    }
    
    dispatch(clearLockInfo());
    previousProjectIdRef.current = projectId;
  }, [projectId]);
  
  // Smart polling with dynamic intervals - single instance across all hook usages
  useEffect(() => {
    if (!projectId || !isProjectIdNumerical) return;
    
    hookInstanceCount++;
    
    const scheduleNextPoll = () => {
      if (!globalPollingTimeout) return;
      
      const interval = Math.min(
        getOptimalInterval(cachedData, currentUser?.email),
        finalConfig.maxInterval
      );
      
      globalPollingTimeout = setTimeout(() => {
        if (currentProjectId === projectId) {
          fetchLockInfo();
          scheduleNextPoll();
        }
      }, interval);
    };
    
    const startGlobalPolling = () => {
      if (globalPollingTimeout && currentProjectId === projectId) {
        return;
      }
      
      if (globalPollingTimeout) {
        clearTimeout(globalPollingTimeout);
        globalPollingTimeout = null;
      }
      
      currentProjectId = projectId;
      scheduleNextPoll();
    };
    
    const stopGlobalPolling = () => {
      hookInstanceCount--;
      
      if (hookInstanceCount === 0 && globalPollingTimeout) {
        clearTimeout(globalPollingTimeout);
        globalPollingTimeout = null;
        currentProjectId = null;
      }
    };
    
    startGlobalPolling();
    
    return stopGlobalPolling;
  }, [projectId, isProjectIdNumerical, cachedData, getOptimalInterval, fetchLockInfo, finalConfig.maxInterval, currentUser?.email]);
  
  // Initial fetch
  useEffect(() => {
    if (!projectId || !isProjectIdNumerical) return;
    
    setIsLoading(true);
    fetchLockInfo().finally(() => setIsLoading(false));
  }, [projectId, isProjectIdNumerical, fetchLockInfo]);
  
  // Manual refetch
  const refetch = useCallback(async () => {
    setIsLoading(true);
    await fetchLockInfo();
    setIsLoading(false);
  }, [fetchLockInfo]);
  
  // Cleanup
  useEffect(() => {
    return () => {
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
    };
  }, []);
  
  // Return processed data with project validation
  const lockInfo = cachedData && cachedData.projectId === Number(projectId)
    ? {
        ...cachedData,
        isStale: isDataStale(cachedData.cachedAt),
      }
    : null;

  const lockProject = useCallback(async () => {
    if (!projectId || !isProjectIdNumerical || lockInfo?.is_locked) return;
    try {
      const result = await toggleProjectLock({ projectId: Number(projectId), isLocked: true }).unwrap();
      if (result) {
        dispatch(setLockInfo({
          projectId: Number(projectId),
          ...result,
          cachedAt: Date.now(),
          isStale: false,
        }));
      }
    } catch (error) {
      console.error('Failed to lock project:', error);
    }
  }, [projectId, isProjectIdNumerical, toggleProjectLock, lockInfo?.is_locked, dispatch]);

  const unlockProject = useCallback(async () => {
    if (!projectId || !isProjectIdNumerical || !lockInfo?.is_locked || lockInfo?.locked_by !== currentUser?.email) return;
    try {
      const result = await toggleProjectLock({ projectId: Number(projectId), isLocked: false }).unwrap();
      if (result) {
        dispatch(setLockInfo({
          projectId: Number(projectId),
          ...result,
          cachedAt: Date.now(),
          isStale: false,
        }));
      }
    } catch (error) {
      console.error('Failed to unlock project:', error);
    }
  }, [projectId, isProjectIdNumerical, toggleProjectLock, lockInfo?.is_locked, lockInfo?.locked_by, currentUser?.email, dispatch]);
  
  return {
    lockInfo,
    isLoading,
    refetch,
    isStale: lockInfo?.isStale || false,
    isUserActive,
    isVisible,
    pollingInterval: getOptimalInterval(cachedData, currentUser?.email),
    lockProject,
    unlockProject,
  };
};
